import React, { useState, type FC } from 'react'
import type { Organization, MemberSession } from '@stytch/vanilla-js'
import type { StytchB2BHeadlessClient } from '@stytch/vanilla-js/b2b/headless'
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b'
import {
  useStytchB2BClient as useStytchB2BClientNext,
  useStytchMemberSession as useStytchMemberSessionNext
} from '@stytch/nextjs/b2b'
import { isNil } from 'lodash'
import { useHotkeys } from 'react-hotkeys-hook'
import { SelectOrganizationModal } from './SelectOrganizationModal'
import { SelectOrganizationInline } from './SelectOrganizationInline'
import { SESSION_DURATION_MINUTES } from '../authentication/constants'

interface Props {
  onSwitch: () => void | Promise<void>
  environment: string
  mode?: 'modal' | 'inline'
  children?: React.ReactNode
  onClose?: () => void
  appType: 'react' | 'nextjs'
}

/**
 * Switch deftly between organizations like a boss. ctrl-alt-shift-t.
 */

export const OrganizationSwitcher: FC<Props> = ({
  environment,
  children,
  onSwitch,
  mode = 'modal',
  onClose,
  appType
}) => {
  const [discoveredOrganizations, setDiscoveredOrganizations] = useState<Array<Organization>>([])
  const [showTenantSwitchModal, setShowTenantSwitchModal] = useState<boolean>(false)

  // Use appropriate hooks based on appType (prop doesn't change, so Rules of Hooks are satisfied)
  let stytch: StytchB2BHeadlessClient
  let session: MemberSession | null

  if (appType === 'nextjs') {
    stytch = useStytchB2BClientNext()
    const sessionHook = useStytchMemberSessionNext()
    session = sessionHook.session
  } else {
    stytch = useStytchB2BClient()
    const sessionHook = useStytchMemberSession()
    session = sessionHook.session
  }

  const discoverStytchOrganizations = async (): Promise<void> => {
    try {
      const response = await stytch.discovery.organizations.list()
      const availableOrganizations = response.discovered_organizations
        .map(({ organization }) => organization)
        .filter(organization => {
          if (
            isNil(organization.trusted_metadata.tenant_ids) ||
            typeof organization.trusted_metadata.tenant_ids !== 'object'
          ) {
            return false
          }
          const tenantIds = organization.trusted_metadata.tenant_ids as Record<string, string>
          const isLocalEnvironment = /local.*/.test(environment)
          const hasTenantInEnvironment = Object.keys(tenantIds).includes(environment)
          return isLocalEnvironment || hasTenantInEnvironment
        })
      setDiscoveredOrganizations(availableOrganizations)
    } catch (error) {
      console.error('Failed to discover organizations:', error)
    }
  }

  // Only enable hotkeys in modal mode
  useHotkeys(
    'Ctrl+Shift+Alt+t, meta+k',
    () => {
      if (mode === 'modal') {
        setShowTenantSwitchModal(true)
        // Refresh organizations when modal is opened to ensure up-to-date list
        void discoverStytchOrganizations()
      }
    },
    {
      preventDefault: true,
      enabled: mode === 'modal'
    }
  )

  const switchOrganization = async (organizationId: string): Promise<void> => {
    await stytch.session.exchange({
      organization_id: organizationId,
      session_duration_minutes: SESSION_DURATION_MINUTES
    })
    void onSwitch()
  }

  const handleClose = () => {
    setShowTenantSwitchModal(false)
    onClose?.()
  }

  // Inline mode - render the organization selector directly
  if (mode === 'inline') {
    return <SelectOrganizationInline organizations={discoveredOrganizations} switchOrganization={switchOrganization} />
  }

  // Modal mode - existing behavior
  return (
    <>
      {showTenantSwitchModal && (
        <SelectOrganizationModal
          organizations={discoveredOrganizations}
          switchOrganization={switchOrganization}
          onClose={handleClose}
        />
      )}
      {children}
    </>
  )
}
